import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Layout from "./layout";
import { doFetch } from "../redux/api/actions";
import { ADMIN_GET_TOURS, ADMIN_GET_USERS, ADMIN_GET_TOURGUIDES, ME } from "../redux/api/constants";
import { useTranslation } from "react-i18next";

function Admin() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const accessToken = useSelector((state) => state.Auth.accessToken);
  const me = useSelector((state) => state.API.me);
  const loading = useSelector((state) => state.API.loading);
  const error = useSelector((state) => state.API.error);
  const adminUsers = useSelector((state) => state.API.adminUsers);
  const adminTours = useSelector((state) => state.API.adminTours);
  const adminTourGuides = useSelector((state) => state.API.adminTourGuides);

  const [userQuery, setUserQuery] = useState("");
  const [tourGuideQuery, setTourGuideQuery] = useState("");
  const [tourQuery, setTourQuery] = useState("");

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString(i18n.language);
  };

  useEffect(() => {
    if (accessToken && !me) {
      dispatch(doFetch(ME, "users/me", accessToken));
    }
  }, [dispatch, accessToken, me]);

  useEffect(() => {
    if (!accessToken) return;
    if (!me?.is_superuser) return;

    dispatch(doFetch(ADMIN_GET_USERS, "users", accessToken));
    dispatch(doFetch(ADMIN_GET_TOURS, "tours", accessToken));
    dispatch(doFetch(ADMIN_GET_TOURGUIDES, "admin/tourguides", accessToken));
  }, [dispatch, accessToken, me?.is_superuser]);

  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return adminUsers || [];
    return (adminUsers || []).filter((u) => {
      const email = (u?.email || "").toLowerCase();
      const id = String(u?.id || "").toLowerCase();
      return email.includes(q) || id.includes(q);
    });
  }, [adminUsers, userQuery]);

  const filteredTours = useMemo(() => {
    const q = tourQuery.trim().toLowerCase();
    if (!q) return adminTours || [];
    return (adminTours || []).filter((t) => {
      const title = (t?.title || "").toLowerCase();
      const id = String(t?.id || "").toLowerCase();
      return title.includes(q) || id.includes(q);
    });
  }, [adminTours, tourQuery]);

  const filteredTourGuides = useMemo(() => {
    const q = tourGuideQuery.trim().toLowerCase();
    if (!q) return adminTourGuides || [];
    return (adminTourGuides || []).filter((tg) => {
      const name = (tg?.name || "").toLowerCase();
      const email = (tg?.email || "").toLowerCase();
      const id = String(tg?.id || "").toLowerCase();
      const userId = String(tg?.user_id || "").toLowerCase();
      return (
        name.includes(q) || email.includes(q) || id.includes(q) || userId.includes(q)
      );
    });
  }, [adminTourGuides, tourGuideQuery]);

  return (
    <Layout selected="admin">
      <nav aria-label="breadcrumb" className="breadcrumb-nav">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">{t('home')}</Link>
          </li>
          <li className="breadcrumb-item active">{t('admin')}</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">{t('admin')}</h1>
          <div className="text-muted small">
            {t('admin-summary', {
              users: adminUsers?.length || 0,
              guides: adminTourGuides?.length || 0,
              tours: adminTours?.length || 0
            })}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-dark"
            onClick={() => {
              if (!accessToken || !me?.is_superuser) return;
              dispatch(doFetch(ADMIN_GET_USERS, "users", accessToken));
              dispatch(doFetch(ADMIN_GET_TOURS, "tours", accessToken));
              dispatch(doFetch(ADMIN_GET_TOURGUIDES, "admin/tourguides", accessToken));
            }}
            disabled={!accessToken || !me?.is_superuser || loading}
          >
            {t('refresh')}
          </button>
        </div>
      </div>

      {!accessToken ? (
        <div className="alert alert-warning">
          {t('admin-signin-required')} <Link to="/signin">{t('signin')}</Link>
        </div>
      ) : me && !me.is_superuser ? (
        <div className="alert alert-danger">
          {t('admin-access-denied')}
        </div>
      ) : null}

      {error ? (
        <div className="alert alert-danger">
          <div className="fw-bold mb-1">{t('error')}</div>
          <div className="small">{String(error)}</div>
        </div>
      ) : null}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">{t('admin-all-users')}</h3>
                <span className="badge bg-secondary">
                  {filteredUsers.length}
                </span>
              </div>

              <input
                className="form-control mb-3"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder={t('admin-search-users')}
                aria-label={t('admin-search-users')}
              />

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>{t('admin-email')}</th>
                      <th className="text-nowrap">{t('admin-signed-up')}</th>
                      <th className="text-nowrap">{t('admin-superuser')}</th>
                      <th className="text-nowrap">{t('admin-active')}</th>
                      <th className="text-nowrap">{t('admin-verified')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id || u.email}>
                        <td className="text-break">
                          <div className="fw-semibold">{u.email}</div>
                          <div className="text-muted small">{u.id}</div>
                        </td>
                        <td className="text-nowrap">{formatDate(u.created_at)}</td>
                        <td>{u.is_superuser ? t('yes') : t('no')}</td>
                        <td>{u.is_active ? t('yes') : t('no')}</td>
                        <td>{u.is_verified ? t('yes') : t('no')}</td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-muted">
                          {t('admin-no-users')}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">{t('admin-all-guides')}</h3>
                <span className="badge bg-secondary">
                  {filteredTourGuides.length}
                </span>
              </div>

              <input
                className="form-control mb-3"
                value={tourGuideQuery}
                onChange={(e) => setTourGuideQuery(e.target.value)}
                placeholder={t('admin-search-guides')}
                aria-label={t('admin-search-guides')}
              />

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>{t('admin-guide')}</th>
                      <th className="text-nowrap">{t('admin-created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTourGuides.map((tg) => (
                      <tr key={tg.id}>
                        <td className="text-break">
                          <div className="fw-semibold">{tg.name || "-"}</div>
                          <div className="text-muted small">{tg.email || "-"}</div>
                          <div className="text-muted small">{t('admin-user')}: {tg.user_id}</div>
                          <div className="text-muted small">{tg.id}</div>
                        </td>
                        <td className="text-nowrap">{formatDate(tg.created_at)}</td>
                      </tr>
                    ))}
                    {filteredTourGuides.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-muted">
                          {t('admin-no-guides')}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="card-title mb-0">{t('admin-all-tours')}</h3>
                <span className="badge bg-secondary">
                  {filteredTours.length}
                </span>
              </div>

              <input
                className="form-control mb-3"
                value={tourQuery}
                onChange={(e) => setTourQuery(e.target.value)}
                placeholder={t('admin-search-tours')}
                aria-label={t('admin-search-tours')}
              />

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>{t('admin-title')}</th>
                      <th className="text-nowrap">{t('admin-created')}</th>
                      <th className="text-nowrap">{t('admin-view')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTours.map((tour) => (
                      <tr key={tour.id}>
                        <td className="text-break">
                          <div className="fw-semibold">{tour.title}</div>
                          <div className="text-muted small">{tour.id}</div>
                        </td>
                        <td className="text-nowrap">{formatDate(tour.created_at)}</td>
                        <td className="text-nowrap">
                          <Link to={`/tours/${tour.id}`}>{t('admin-open')}</Link>
                        </td>
                      </tr>
                    ))}
                    {filteredTours.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-muted">
                          {t('admin-no-tours')}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Admin;
